<template>
  <el-dialog
    :title="isView ? '用户类型详情' : '新增用户类型'"
    :visible.sync="visible"
    width="640px"
    :destroy-on-close="true"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="110px"
      size="small"
      :disabled="isView"
    >
      <el-form-item label="用户类型昵称" prop="typeName">
        <el-input
          v-model.trim="form.typeName"
          placeholder="请输入用户类型昵称"
          maxlength="50"
          show-word-limit
        />
      </el-form-item>

      <div
        v-for="(rule, index) in form.rules"
        :key="index"
        class="rule-block"
      >
        <div class="rule-header">
          <span>类型 {{ index + 1 }}</span>
          <el-button
            v-if="!isView && index > 0"
            type="text"
            size="mini"
            class="del-btn"
            @click="removeRule(index)"
          >
            删除
          </el-button>
        </div>

        <el-form-item
          label="类型"
          :prop="'rules.' + index + '.ruleType'"
          :rules="ruleTypeRules"
        >
          <el-select
            v-model="rule.ruleType"
            placeholder="请选择类型"
            style="width: 100%"
            @change="onRuleTypeChange(index)"
          >
            <el-option
              v-for="item in ruleTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="needUidText(rule.ruleType)"
          :label="uidLabel(rule.ruleType)"
          :prop="'rules.' + index + '.uidText'"
          :rules="uidTextRules"
        >
          <el-input
            v-model.trim="rule.uidText"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
            placeholder="多个UID用英文逗号分隔"
          />
        </el-form-item>

        <el-form-item
          v-if="needRuleTime(rule.ruleType)"
          label="自定义时间"
          :prop="'rules.' + index + '.ruleTime'"
          :rules="ruleTimeRules"
        >
          <el-date-picker
            v-model="rule.ruleTime"
            type="datetime"
            placeholder="请选择时间"
            value-format="timestamp"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item
          v-if="!isView && index === 0 && form.rules.length === 2"
          label="关系"
        >
          <el-radio-group v-model="form.logicBetween">
            <el-radio :label="1">且</el-radio>
            <el-radio :label="2">或</el-radio>
          </el-radio-group>
          <div class="logic-tip">
            且：同时满足所有类型；或：满足其中任一类型
          </div>
        </el-form-item>

        <div
          v-if="isView && index === 0 && form.rules.length === 2"
          class="logic-view"
        >
          关系：{{ form.logicBetween === 1 ? '且' : '或' }}
        </div>
      </div>

      <div v-if="!isView && form.rules.length < 2" class="add-rule">
        <el-button type="text" icon="el-icon-plus" @click="addRule">
          新增类型（最多2个）
        </el-button>
      </div>
    </el-form>

    <div slot="footer" v-if="!isView">
      <el-button size="small" @click="handleClose">取消</el-button>
      <el-button size="small" type="primary" :loading="submitting" @click="submit">
        确认
      </el-button>
    </div>
    <div slot="footer" v-else>
      <el-button size="small" type="primary" @click="handleClose">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import api from '../api'
import {
  RULE_TYPE_OPTIONS,
  LOGIC_TYPE,
  needUidText,
  needRuleTime,
  createEmptyRule,
} from '../constants'

function createDefaultForm() {
  return {
    typeName: '',
    logicBetween: LOGIC_TYPE.AND,
    rules: [createEmptyRule(1)],
  }
}

export default {
  name: 'UserTypeFormDialog',
  props: {
    value: { type: Boolean, default: false },
    mode: { type: String, default: 'add' }, // add | view
    userTypeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      submitting: false,
      form: createDefaultForm(),
      ruleTypeOptions: RULE_TYPE_OPTIONS,
      rules: {
        typeName: [
          { required: true, message: '请输入用户类型昵称', trigger: 'blur' },
          { max: 50, message: '最多50个字符', trigger: 'blur' },
        ],
      },
      ruleTypeRules: [
        { required: true, message: '请选择类型', trigger: 'change' },
      ],
      uidTextRules: [
        { required: true, message: '请输入UID', trigger: 'blur' },
        { max: 1000, message: '最多1000个字符', trigger: 'blur' },
      ],
      ruleTimeRules: [
        { required: true, message: '请选择自定义时间', trigger: 'change' },
      ],
    }
  },
  computed: {
    visible: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    isView() {
      return this.mode === 'view'
    },
  },
  watch: {
    value(open) {
      if (open) this.initForm()
    },
  },
  methods: {
    needUidText,
    needRuleTime,
    uidLabel(ruleType) {
      if (ruleType === 'SPECIFIED_PARTNER') return '合伙人UID'
      return '用户UID'
    },
    initForm() {
      this.form = createDefaultForm()
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
      if (this.mode === 'view' && this.userTypeId) {
        this.loadDetail()
      }
    },
    loadDetail() {
      api.detail({ id: this.userTypeId }).then((res) => {
        if (res && res.code === 0) {
          const d = res.data || {}
          const rules = (d.rules || []).map((item, idx) => ({
            ruleType: item.ruleType || '',
            uidText: item.uidText || '',
            ruleTime: item.ruleTime != null ? Number(item.ruleTime) : null,
            sortNo: item.sortNo || idx + 1,
            logicType: item.logicType != null ? item.logicType : LOGIC_TYPE.NONE,
          }))
          const firstLogic =
            rules.length > 1 && rules[0].logicType
              ? rules[0].logicType
              : LOGIC_TYPE.AND
          this.form = {
            typeName: d.typeName || '',
            logicBetween: firstLogic,
            rules: rules.length ? rules : [createEmptyRule(1)],
          }
        } else {
          this.$message.error((res && res.msg) || '获取详情失败')
        }
      })
    },
    onRuleTypeChange(index) {
      const rule = this.form.rules[index]
      if (!needUidText(rule.ruleType)) rule.uidText = ''
      if (!needRuleTime(rule.ruleType)) rule.ruleTime = null
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate([
            `rules.${index}.uidText`,
            `rules.${index}.ruleTime`,
          ])
        }
      })
    },
    addRule() {
      if (this.form.rules.length >= 2) return
      this.form.rules.push(createEmptyRule(2))
    },
    removeRule(index) {
      if (index <= 0) return
      this.form.rules.splice(index, 1)
      this.form.rules[0].sortNo = 1
    },
    buildPayload() {
      const rules = this.form.rules.map((rule, index) => {
        const item = {
          ruleType: rule.ruleType,
          sortNo: index + 1,
          logicType:
            index === 0 && this.form.rules.length === 2
              ? this.form.logicBetween
              : LOGIC_TYPE.NONE,
        }
        if (needUidText(rule.ruleType)) {
          item.uidText = rule.uidText
        }
        if (needRuleTime(rule.ruleType)) {
          item.ruleTime = Number(rule.ruleTime)
        }
        return item
      })
      return {
        typeName: this.form.typeName,
        rules,
      }
    },
    handleClose() {
      this.visible = false
    },
    submit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.submitting = true
        api
          .add(this.buildPayload())
          .then((res) => {
            if (res && res.code === 0) {
              this.$message.success('新增成功')
              this.$emit('success')
              this.handleClose()
            } else {
              this.$message.error((res && res.msg) || '新增失败')
            }
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },
  },
}
</script>

<style lang="less" scoped>
.rule-block {
  margin-bottom: 12px;
  padding: 12px 12px 4px;
  background: #f7f8fc;
  border-radius: 6px;
}
.rule-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 500;
  color: #303133;
}
.del-btn {
  color: #f56c6c;
}
.add-rule {
  margin-left: 110px;
}
.logic-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.logic-view {
  margin: 0 0 12px 110px;
  color: #606266;
  font-size: 13px;
}
</style>
